import * as S from '@unionfab/ufc-shop-commons';
import {
  getCurrencySymbolByType,
  i18nFormat,
} from '@unionfab/ufc-shop-commons';
import { Spin, Table, Input, Form } from 'antd';
import cn from 'classnames';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import {
  getUseShopQuoteStoreByCode,
  LK_SELECTED_COUPON_CODE,
  ShopOrderQuoteStoreModifiers,
  useShopExpressStore,
} from '../../../../../../stores';
import { DeliveryTimeTitle } from '../../../../components/info/DeliveryTimeTitle';
import styles from './index.module.less';
import {
  ExclamationCircleOutlined,
  Loading3QuartersOutlined,
} from '@ant-design/icons';

export interface ShopQuoteShippingMethodMgtProps {
  className?: string;
  style?: Record<string, string | number>;

  orderCode: string;
}

export const ShopQuoteShippingMethodMgt = ({
  className,
  style,

  orderCode,
}: ShopQuoteShippingMethodMgtProps) => {
  const shopOrderStore = getUseShopQuoteStoreByCode(orderCode);
  const {
    isLoading,
    allowEdit,
    shopOrder,
    orderPrice,
    quotation,
    shopOrderPrice,
    ratioFromUsdToOrderCurrency,
  } = shopOrderStore(s => ({
    isLoading: s.isLoading,
    shopOrder: s.shopOrder,
    allowEdit: s.allowEdit,
    orderPrice: s.shopOrderPrice,
    quotation: s.shopOrderQuotation,
    shopOrderPrice: s.shopOrderPrice,
    ratioFromUsdToOrderCurrency: s.ratioFromUsdToOrderCurrency,
  }));

  const isUSAddress = useMemo(() => {
    const { delivery } = shopOrder;
    const { items } = delivery;
    if (!S.isValidArray(items)) return false;
    return (
      items.filter(
        i =>
          i.country == 'American Samoa' ||
          i.country == 'United States' ||
          i.country == 'United States Minor Outlying Islands',
      ).length > 0
    );
  }, [shopOrder]);

  const [preAddress, setPreAddress] = useState<
    S.ShopOrderDeliveryItem | S.ShopOrderDeliveryItemRequestParams
  >();
  const [form] = Form.useForm();

  useEffect(() => {
    shopOrderStore.setState({ fedexForm: form });
    return () => {
      shopOrderStore.setState({ fedexForm: null });
    };
  }, [form]);

  const delivery = S.get(shopOrder, s => s.delivery);

  useEffect(() => {
    const currentVal = form.getFieldValue('fedexAccount');
    if (currentVal !== delivery.fedexAccount) {
      form.setFieldsValue({ fedexAccount: delivery.fedexAccount });
    }
  }, [delivery.fedexAccount, form]);
  const deliveryAddress = S.get(shopOrder, () => shopOrder.delivery.items[0]);

  const { expressList, loadExpress } = useShopExpressStore(s => ({
    expressList: s.shopExpresses,
    loadExpress: s.loadShopExpresses,
  }));

  const selectedExpressId = S.get(
    shopOrder,
    s => s.requirements.customerSelections.delivery.expressId,
  );

  const selectedExpress = expressList.find(e => e.id === selectedExpressId);
  const isFedexSelected = selectedExpress?.name
    ?.toUpperCase()
    .includes('FEDEX');

  const dataSoure = useMemo(() => {
    if (!S.isValidArray(expressList)) return [];

    const expressIds = S.get(quotation, q => q.delivery.prices, []).map(
      e => e.expressId,
    );

    /** 待客户确认状态只返回已选物流信息 */
    if (shopOrder && shopOrder.status == 'WAIT_CUSTOM_CONFIRM') {
      const currentId = S.get(
        shopOrder,
        o => o.requirements.customerSelections.delivery.expressId,
      );

      return expressList.filter(e => e.id == currentId);
    }

    return expressList;

    // return expressList.filter(e => expressIds.includes(e.id));
  }, [expressList, quotation, shopOrder]);

  useEffect(() => {
    const expressId = S.get(
      shopOrder,
      o => o.requirements.customerSelections.delivery.expressId,
    );

    /** 有可选物流但订单没选择物流时默认选中第一个物流 */
    if (S.isValidArray(dataSoure) && !expressId && !isLoading) {
      ShopOrderQuoteStoreModifiers.updateOrder({
        orderCode,
        onChange: o => {
          o.requirements.customerSelections.delivery.expressId =
            dataSoure[0].id;
        },
      });
    }

    /** 当前所选物流不在 dataSoure 内时更新订单 */
    if (
      expressId &&
      !isLoading &&
      S.isValidArray(dataSoure) &&
      !(dataSoure || []).find(a => a.id == expressId)
    ) {
      ShopOrderQuoteStoreModifiers.updateOrder({
        orderCode,
        onChange: o => {
          o.requirements.customerSelections.delivery.expressId = null;
        },
      });
    }
  }, [dataSoure, shopOrder, isLoading]);

  const expressDisabled = useMemo(() => {
    const { deliveryPricingWeight } = quotation;
    const calcBox = S.get(deliveryPricingWeight, i => i.calcBox);
    const volumeWeight = S.get(
      deliveryPricingWeight,
      i => i.conformedWeightInKg,
    );
    const sizeX = S.get(calcBox, i => i.sizeX);
    const sizeY = S.get(calcBox, i => i.sizeY);
    const sizeZ = S.get(calcBox, i => i.sizeZ);

    let max = 0;

    // 最大变长限制
    if (sizeX && sizeY && sizeZ) {
      max = Math.max(Number(sizeY), Number(sizeX), Number(sizeZ));
    }
    if (max > 500) {
      return true;
    }
    // 重量限制
    if (volumeWeight > 4) {
      return true;
    }
    // 周长限制
    const perimeter = sizeY + 2 * sizeX + 2 * sizeZ;
    if (perimeter > 2100) {
      return true;
    }
    // 金额限制
    // if (shopOrderPrice.production.price.amount > 60) {
    //   return true;
    // }
  }, [selectedExpress, shopOrderPrice, quotation]);

  useEffect(() => {
    if (
      !!deliveryAddress &&
      (!preAddress || !isEqual(deliveryAddress, preAddress))
    ) {
      setPreAddress(deliveryAddress);

      deliveryAddress.country && loadExpress(deliveryAddress.country);
    }
  }, [deliveryAddress, preAddress]);

  const isPriceDisabled = (rowData: S.ShopExpress) => {
    const delivery = S.get(quotation, q => q.delivery);

    if (!delivery) return false;
    const targetQuotation = (delivery.prices || []).find(
      p => p.expressId === rowData.id,
    );

    const price = S.get(targetQuotation, t => t.price);

    // 获取当前订单的币种
    const orderCurrency = S.get(
      shopOrderPrice,
      p => p.production?.price?.currency,
    );

    // 计算总价格（生产费用 + 物流费用）
    const totalAmount =
      shopOrderPrice?.production?.price?.amount + (price?.amount || 0);

    // 如果是美元，直接比较100
    if (orderCurrency === 'USD') {
      return totalAmount > 100;
    }

    // 如果不是美元，需要转换为美元进行比较
    // ratioFromUsdToOrderCurrency 是从美元到订单币种的汇率
    // 所以 100 美元 = 100 * ratioFromUsdToOrderCurrency 订单币种
    const usdEquivalentInOrderCurrency =
      100 * (ratioFromUsdToOrderCurrency || 1);

    return totalAmount > usdEquivalentInOrderCurrency;
  };

  return (
    <div
      id="ShopQuoteShippingMethodMgt"
      className={cn(className, styles.container)}
      style={style}
    >
      <Table<S.ShopExpress>
        rowKey="id"
        pagination={false}
        expandable={{
          expandedRowKeys: [
            ...(isFedexSelected ? [selectedExpressId] : []),
            ...dataSoure
              .filter(
                r =>
                  r.name.includes('Tariff-Free Shipping') &&
                  (expressDisabled || isPriceDisabled(r)),
              )
              .map(r => r.id),
          ],
          showExpandColumn: false,
          expandedRowRender: record => {
            if (record.name?.toUpperCase().includes('FEDEX')) {
              return (
                <div
                  style={{ padding: '8px 16px', backgroundColor: '#fafafa' }}
                >
                  {isUSAddress ? (
                    <Form
                      form={form}
                      layout="vertical"
                      initialValues={{
                        fedexAccount: delivery.fedexAccount,
                      }}
                      onValuesChange={changedValues => {
                        if ('fedexAccount' in changedValues) {
                          shopOrderStore.setState({
                            shopOrder: {
                              ...shopOrder,
                              delivery: {
                                ...shopOrder.delivery,
                                fedexAccount: changedValues.fedexAccount,
                              },
                            },
                          });
                        }
                      }}
                    >
                      <Form.Item
                        label={i18nFormat("Recipient's FedEx Account Number")}
                        name="fedexAccount"
                        normalize={value => {
                          if (!value) return value;
                          // 只允许输入数字且最多 9 位
                          return value.replace(/[^\d]/g, '').slice(0, 9);
                        }}
                        rules={[
                          {
                            required: true,
                            message: i18nFormat(
                              "Please enter Recipient's FedEx Account Number",
                            ),
                          },
                          {
                            pattern: /^\d{9}$/,
                            message: i18nFormat(
                              'Please enter a 9-digit FedEx account number',
                            ),
                          },
                        ]}
                      >
                        <Input
                          placeholder={i18nFormat(
                            "Please enter Recipient's FedEx Account Number",
                          )}
                          maxLength={9}
                          inputMode="numeric"
                          disabled={isLoading || !allowEdit}
                        />
                      </Form.Item>
                      <div
                        style={{
                          marginTop: '8px',
                          color: '#666',
                          fontSize: '14px',
                          lineHeight: '1.5',
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            marginRight: '6px',
                            marginTop: '2px',
                            color: '#1890ff',
                            verticalAlign: 'top',
                          }}
                        >
                          <ExclamationCircleOutlined
                            style={{ color: '#000' }}
                          />
                        </span>
                        <span style={{ display: 'inline' }}>
                          {i18nFormat(
                            "US packages require the recipient's FedEx account number to be shipped. FedEx will charge an additional $14 surcharge for orders over $50.",
                          )}
                        </span>
                      </div>
                    </Form>
                  ) : (
                    <div
                      style={{
                        marginTop: '8px',
                        color: '#666',
                        fontSize: '14px',
                        lineHeight: '1.5',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-block',
                          marginRight: '6px',
                          marginTop: '2px',
                          color: '#1890ff',
                          verticalAlign: 'top',
                        }}
                      >
                        <ExclamationCircleOutlined style={{ color: '#000' }} />
                      </span>
                      <span style={{ display: 'inline' }}>
                        {i18nFormat(
                          'For orders over $50, FedEx will charge a $14 export customs surcharge',
                        )}
                      </span>
                    </div>
                  )}
                </div>
              );
            }

            // Tariff-Free Shipping物流限制提示 - 在满足禁用条件时显示
            if (
              record.name.includes('Tariff-Free Shipping') &&
              (expressDisabled || isPriceDisabled(record))
            ) {
              return (
                <div
                  style={{
                    padding: '8px 16px',
                    color: '#ff4d4f',
                    fontSize: '14px',
                  }}
                >
                  {`Logistics Provider Restrictions：Maximum Length: <= 50cm; Peripheral Length: <= 2100cm; Weight: <= 4kg; Amount: <= $100`}
                </div>
              );
            }

            return undefined;
          },
          rowExpandable: record =>
            record.name?.toUpperCase().includes('FEDEX') ||
            (record.name.includes('Tariff-Free Shipping') &&
              (expressDisabled || isPriceDisabled(record))),
        }}
        columns={[
          {
            title: i18nFormat('Shipping Method'),
            key: 'name',
            dataIndex: 'name',
          },
          {
            title: i18nFormat('Costs'),
            key: 'costs',
            render: (_, e) => {
              const delivery = S.get(quotation, q => q.delivery);

              if (!delivery) return '-';

              const targetQuotation = (delivery.prices || []).find(
                p => p.expressId === e.id,
              );

              const price = S.get(targetQuotation, t => t.price);
              const currency = S.get(price, p => p.currency);
              const currencySymbol = getCurrencySymbolByType(currency);

              if (S.get(delivery, t => t.status) == 'COMPUTING') {
                return (
                  <span>
                    {currencySymbol}
                    <Spin
                      size="small"
                      indicator={<Loading3QuartersOutlined spin />}
                      style={{ marginLeft: 4, marginRight: 4 }}
                    />
                    Calculating Prices
                  </span>
                );
              }

              if (
                S.get(delivery, t => t.status) == 'RFQ' ||
                typeof S.get(price, p => p.amount) !== 'number'
              ) {
                return 'RFQ';
              }

              return S.toPriceStringWithCurrency(price);
            },
          },
          {
            title: <DeliveryTimeTitle />,
            key: 'deliveryStr',
            render: (_, e) => {
              return e.deliveryStr;
            },
          },
          {
            title: i18nFormat('Restriction'),
            key: 'restrictionDescription',
            render: (_, e) => {
              return e.restrictionDescription;
            },
          },
        ]}
        dataSource={dataSoure}
        rowSelection={{
          type: 'radio',
          selectedRowKeys: [selectedExpressId],
          getCheckboxProps: r => ({
            disabled:
              isLoading ||
              (!allowEdit &&
                orderPrice &&
                orderPrice.status !== 'OK' &&
                shopOrder.status == 'WAIT_REVIEW') ||
              (r.name.includes('Tariff-Free Shipping') &&
                (expressDisabled || isPriceDisabled(r))),
          }),
          onSelect: r => {
            /** 切换物流公司前先清空优惠券信息 */
            shopOrderStore.setState({ couponCode: null });
            localStorage.removeItem(`${LK_SELECTED_COUPON_CODE}_${orderCode}`);

            ShopOrderQuoteStoreModifiers.updateOrder({
              orderCode,
              onChange: o => {
                o.requirements.customerSelections.delivery.expressId = r.id;
              },
            });

            // 如果不是FedEx，清空账号信息
            if (!r.name?.toUpperCase().includes('FEDEX')) {
              // TODO 清除fedexAccount
              form.resetFields();
              shopOrderStore.setState({
                shopOrder: {
                  ...shopOrder,
                  delivery: {
                    ...shopOrder.delivery,
                    fedexAccount: '',
                  },
                },
              });
            }
          },
        }}
      />
    </div>
  );
};

ShopQuoteShippingMethodMgt.displayName = 'ShopQuoteShippingMethodMgt';
